import { motion } from "framer-motion";
import { Check, X, AlertTriangle } from "lucide-react";

/**
 * ComparisonTable – SuperMajstri vs. bežná stavebná firma.
 *
 * Rieši psychologickú bariéru pred prvým kontaktom: zákazník má strach
 * z poddodávok, skrytých cien a mizernej záruky. Táto tabuľka ich pomenuje
 * a ukáže, že u nás dostane to, čo iní nedávajú.
 */
const ROWS = [
  {
    label: "Fixná cena v zmluve",
    us: { ok: true, note: "Bez dodatočných príplatkov po podpise" },
    them: { ok: false, note: "Bežne 15–30 % navyše počas realizácie" },
  },
  {
    label: "Denná fotodokumentácia",
    us: { ok: true, note: "Denne cez WhatsApp/email – vidíte postup aj z práce" },
    them: { ok: false, note: "Nič, alebo len na konci pri odovzdaní" },
  },
  {
    label: "Vlastný tím murárov a remeselníkov",
    us: { ok: true, note: "Bez poddodávok – kontrolujeme kvalitu" },
    them: { ok: false, note: "Väčšinou externí subdodávatelia" },
  },
  {
    label: "Záruka na práce",
    us: { ok: true, note: "36 mesiacov (3 roky) písomne" },
    them: { warn: true, note: "Zvyčajne 24 mesiacov, často len ústne" },
  },
  {
    label: "Poistenie zodpovednosti",
    us: { ok: true, note: "Finančné krytie do 100 000 €" },
    them: { warn: true, note: "Ak áno, poistky často nekryjú škody" },
  },
  {
    label: "Materiály od overených značiek",
    us: { ok: true, note: "Baumit, Wienerberger, Rockwool, Ytong…" },
    them: { warn: true, note: "Neznáme značky pre nižšiu maržu" },
  },
  {
    label: "Odozva na dopyt",
    us: { ok: true, note: "Do 24 h online / do 2 h telefonicky" },
    them: { warn: true, note: "Zvyčajne 3–7 dní, občas žiadna" },
  },
  {
    label: "Cenová ponuka s rozpisom",
    us: { ok: true, note: "Detailne po položkách, platná 30 dní" },
    them: { warn: true, note: "Jedno paušálne číslo bez rozpisu" },
  },
  {
    label: "Zapísaná firma v OR SR",
    us: { ok: true, note: "RESTOB s.r.o. – overiteľná v Obchodnom registri" },
    them: { warn: true, note: "Živnostník bez ručenia majetkom firmy" },
  },
  {
    label: "AI Foto-Odhad ceny za 30 sekúnd",
    us: { ok: true, note: "Nahrajte foto, dostanete rozpätie ceny hneď" },
    them: { ok: false, note: "Musíte čakať na obhliadku dní alebo týždne" },
  },
];

const CellIcon = ({ cell }) => {
  if (cell.ok) return <Check size={18} className="text-emerald-400 shrink-0" />;
  if (cell.warn) return <AlertTriangle size={18} className="text-amber-400 shrink-0" />;
  return <X size={18} className="text-rose-400 shrink-0" />;
};

const ComparisonTable = () => {
  return (
    <section
      id="porovnanie"
      className="py-24 bg-black relative overflow-hidden"
      data-testid="comparison-section"
    >
      {/* Subtle glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full opacity-[0.06] blur-3xl"
        style={{ background: "radial-gradient(circle, #00E5FF, transparent)" }}
      />

      <div className="max-w-6xl mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <div className="inline-block px-3 py-1 bg-[#00E5FF]/10 border border-[#00E5FF]/20 rounded-full text-xs text-[#00E5FF] font-semibold tracking-wider uppercase mb-4">
            Prečo my
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            SuperMajstri <span className="text-zinc-500">vs.</span>{" "}
            <span className="text-zinc-400">bežná firma</span>
          </h2>
          <p className="mt-4 text-zinc-300 max-w-2xl">
            Toto sú body, kde zákazníci najčastejšie strácajú peniaze aj
            nervy. Otvorene ukazujeme, kde robíme veci inak.
          </p>
        </motion.div>

        {/* Table */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden backdrop-blur-sm">
          {/* Header row */}
          <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr] gap-0 border-b border-white/10 bg-white/[0.03]">
            <div className="hidden sm:block px-6 py-4 text-xs uppercase tracking-widest text-zinc-500 font-semibold">
              Kritérium
            </div>
            <div className="px-6 py-4 border-l border-white/10 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#00E5FF] shadow-[0_0_10px_#00E5FF]"></span>
              <span className="font-bold text-white">SuperMajstri</span>
            </div>
            <div className="px-6 py-4 border-l border-white/10 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-zinc-500"></span>
              <span className="font-semibold text-zinc-400">Bežná firma</span>
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((row, i) => (
            <motion.div
              key={row.label}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03, duration: 0.4 }}
              className={`grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr] gap-0 border-t border-white/5 ${
                i % 2 === 0 ? "bg-transparent" : "bg-white/[0.015]"
              }`}
              data-testid={`comparison-row-${i}`}
            >
              <div className="px-6 py-5 flex items-center">
                <div className="text-sm font-semibold text-zinc-200">
                  {row.label}
                </div>
              </div>
              <div className="px-6 py-5 border-l border-white/5 bg-[#00E5FF]/[0.03]">
                <div className="flex items-start gap-3">
                  <CellIcon cell={row.us} />
                  <div className="text-sm text-zinc-200 leading-snug">
                    {row.us.note}
                  </div>
                </div>
              </div>
              <div className="px-6 py-5 border-l border-white/5">
                <div className="flex items-start gap-3">
                  <CellIcon cell={row.them} />
                  <div className="text-sm text-zinc-400 leading-snug">
                    {row.them.note}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Estimated savings callout */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#00E5FF]/10 to-transparent border border-[#00E5FF]/20"
          data-testid="comparison-savings"
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-semibold mb-2">
            Reálna úspora
          </div>
          <div className="text-lg sm:text-xl text-white leading-relaxed">
            Ak vás priemerný projekt stojí <span className="font-bold text-white">15 000 €</span>,
            s nami ušetríte na skrytých príplatkoch až{" "}
            <span className="font-bold text-[#00E5FF]">2 250 – 4 500 €</span>.
            <span className="block text-sm text-zinc-400 mt-1">
              (15 – 30 % z bežných dodatočných navýšení počas realizácie)
            </span>
          </div>
        </motion.div>

        {/* CTA under table */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
        >
          <div className="text-sm text-zinc-400 max-w-lg">
            <span className="text-white font-semibold">15+ rokov</span> a{" "}
            <span className="text-white font-semibold">500+ dokončených projektov</span>
            {" "}v Bratislavskom a Trnavskom kraji. Nechajte si spraviť
            nezáväznú ponuku – uvidíte rozdiel na papieri.
          </div>
          <a
            href="#kontakt"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E5FF] hover:bg-[#33EEFF] text-black font-semibold rounded-xl transition-colors"
            data-testid="comparison-cta"
          >
            Chcem nezáväznú ponuku
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonTable;
